/**
 * @fileoverview Add-evidence dialog for a POD claim.
 *
 * Purpose: the two ways a practitioner puts a document behind a claim —
 * upload a new file, or link one already in the job library. Either way
 * the document is attached to the claim and its extraction is routed to
 * this claim, so the extracted invoice / statement lines land here rather
 * than wherever the creditor-name match would have sent them.
 *
 * Dependencies:
 * - useLibraryUpload (the job's upload contract, returns the new doc ids)
 * - api.jobs.recoveries.publicQueries.listClaimDocumentCandidates
 * - api.jobs.recoveries.publicMutations.attachClaimDocument
 *
 * Used by:
 * - PodClaimPanel (Source Documents section and the line-item sections)
 */

import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Check, FileText, Loader2, Sparkles, Upload } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { Button } from "@/ui/primitives/button";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { useLibraryUpload } from "../gather/library/useLibraryUpload";

export function ClaimEvidenceDialog({
  open,
  onOpenChange,
  claimId,
  jobId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claimId: Id<"podClaims">;
  jobId: Id<"jobs">;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const { handleDrop, handlePickedFiles, uploadingCount } =
    useLibraryUpload(jobId);

  const attach = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.attachClaimDocument,
    ),
  });

  const { data: candidates } = useQuery({
    ...convexQuery(
      api.jobs.recoveries.publicQueries.listClaimDocumentCandidates,
      { claimId },
    ),
    enabled: open,
  });

  /** Attaches every uploaded document, reporting the first failure only. */
  async function attachAll(documentIds: Id<"documents">[]) {
    if (documentIds.length === 0) return;
    for (const documentId of documentIds) {
      await attach.mutateAsync({ claimId, documentId });
    }
    toast.success(
      documentIds.length === 1
        ? "Document attached — Morgan is reading it for claim lines"
        : `${documentIds.length} documents attached — Morgan is reading them for claim lines`,
    );
    onOpenChange(false);
  }

  async function runUpload(action: () => Promise<Id<"documents">[]>) {
    setBusy(true);
    try {
      await attachAll(await action());
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to attach the document"));
    } finally {
      setBusy(false);
    }
  }

  async function linkExisting(documentId: Id<"documents">) {
    setBusy(true);
    try {
      await attach.mutateAsync({ claimId, documentId });
      toast.success("Document attached — Morgan is reading it for claim lines");
      onOpenChange(false);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to attach the document"));
    } finally {
      setBusy(false);
    }
  }

  const isWorking = busy || uploadingCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add evidence</DialogTitle>
          <DialogDescription>
            Attach evidence of the claim. Morgan will add claim lines for you.
            Always crosscheck against the source before adjudicating.
          </DialogDescription>
        </DialogHeader>

        {/* `min-w-0`: DialogContent is a grid, whose items size to their
            max-content width by default — without this the document list
            stretches the panel past the dialog on long file names. */}
        <Tabs defaultValue="upload" className="min-w-0">
          <TabsList className="w-full">
            <TabsTrigger value="upload" className="flex-1 gap-1.5">
              <Upload className="h-3.5 w-3.5" /> Upload a file
            </TabsTrigger>
            <TabsTrigger value="existing" className="flex-1 gap-1.5">
              <FileText className="h-3.5 w-3.5" /> Link an existing file
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="mt-4">
            <button
              type="button"
              disabled={isWorking}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                const { dataTransfer } = e;
                void runUpload(() => handleDrop(dataTransfer));
              }}
              className={cn(
                "flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center transition-colors",
                isDragOver
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/50",
                isWorking && "pointer-events-none opacity-60",
              )}
            >
              {isWorking ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-5 w-5 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">
                {isWorking
                  ? "Uploading…"
                  : "Drop a file here, or click to browse"}
              </span>
              <span className="text-xs text-muted-foreground">
                The file is added to the job library and attached to this claim.
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = "";
                if (files.length > 0) {
                  void runUpload(() => handlePickedFiles(files));
                }
              }}
            />
            <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Sparkles className="mt-0.5 h-3 w-3 shrink-0" />
              Extraction runs in the background — the lines appear on the claim
              once it finishes.
            </p>
          </TabsContent>

          <TabsContent value="existing" className="mt-4 min-w-0">
            <Command className="w-full rounded-lg border bg-transparent">
              <CommandInput placeholder="Search job documents..." />
              <CommandList className="max-h-72 overscroll-contain">
                <CommandEmpty>
                  {candidates === undefined
                    ? "Loading documents…"
                    : "No matching document."}
                </CommandEmpty>
                {(candidates ?? []).map((candidate) => {
                  const unavailable =
                    candidate.attachedHere || !!candidate.attachedElsewhere;
                  return (
                    <CommandItem
                      key={candidate.documentId}
                      value={candidate.fileName}
                      disabled={unavailable || isWorking}
                      className="min-w-0"
                      onSelect={() => {
                        if (unavailable) return;
                        void linkExisting(candidate.documentId);
                      }}
                    >
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span
                        className="min-w-0 flex-1 truncate"
                        title={candidate.fileName}
                      >
                        {candidate.fileName}
                      </span>
                      {candidate.attachedHere ? (
                        <span className="flex shrink-0 items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400">
                          <Check className="h-3 w-3" /> Attached
                        </span>
                      ) : candidate.attachedElsewhere ? (
                        <span
                          className="max-w-[110px] shrink-0 truncate text-[10px] text-muted-foreground"
                          title={`Already attached to the claim for ${candidate.attachedElsewhere}`}
                        >
                          On {candidate.attachedElsewhere}
                        </span>
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandList>
            </Command>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            disabled={isWorking}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
